import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Catches a render crash and shows something readable.
 *
 * Without this, a thrown error unmounts the whole React tree and the user is
 * left staring at a white screen with no idea what happened and nothing to
 * report back. That is what "save leads to a blank page" looks like from the
 * field. Now the error is named on screen and kept in localStorage, so it can be
 * read back afterwards even if nobody had a console open.
 */
const LAST_ERROR_KEY = 'keventer_last_error';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // eslint-disable-next-line no-console
    console.error('Unhandled render error:', error, info);
    try {
      localStorage.setItem(LAST_ERROR_KEY, JSON.stringify({
        message: String(error?.message || error),
        stack: String(error?.stack || '').split('\n').slice(0, 8).join('\n'),
        componentStack: String(info?.componentStack || '').split('\n').slice(0, 8).join('\n'),
        at: new Date().toISOString(),
        path: window.location?.pathname,
      }));
    } catch (storageError) {
      // Storage may be unavailable; the on-screen message still stands.
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    const message = String(this.state.error?.message || this.state.error);

    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-lg w-full bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-slate-900">Something went wrong</h1>
              <p className="text-sm text-slate-500">
                Your work up to this point was saved if you had already pressed Save.
              </p>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
            <p className="text-xs font-medium text-slate-500 mb-1">What broke</p>
            <p className="text-sm text-slate-800 break-words">{message}</p>
          </div>

          <p className="text-xs text-slate-500">
            Please send this message to your administrator — it says exactly what failed.
          </p>

          <div className="flex gap-3 pt-1">
            <Button
              variant="outline"
              className="flex-1 h-11 rounded-xl"
              onClick={() => window.location.reload()}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reload
            </Button>
            <Button
              className="flex-1 h-11 rounded-xl bg-sky-600 hover:bg-sky-700 text-white"
              onClick={() => { window.location.href = '/dashboard'; }}
            >
              <Home className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
